import React, { useState, useEffect } from 'react'
import { Walk, WalkActivity, Waypoint } from '../../types/walk'
import { walkService } from '../../services/walks'
import { activityService } from '../../services/activities'
import WaypointManager from './WaypointManager'

interface WalkManagementProps {
  walks: Walk[]
  setWalks: React.Dispatch<React.SetStateAction<Walk[]>>
  clearActivitiesCache?: boolean
}

const WalkManagement: React.FC<WalkManagementProps> = ({ walks, setWalks, clearActivitiesCache }) => {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newWalkName, setNewWalkName] = useState('')
  const [newWalkExpectedKm, setNewWalkExpectedKm] = useState<number | undefined>(undefined)
  const [isCreating, setIsCreating] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedWalkId, setExpandedWalkId] = useState<string | null>(null)
  const [walkActivities, setWalkActivities] = useState<Record<string, WalkActivity[]>>({})
  const [loadingActivities, setLoadingActivities] = useState<Record<string, boolean>>({})
  const [editingWalkId, setEditingWalkId] = useState<string | null>(null)
  const [editedWalkName, setEditedWalkName] = useState('')
  const [editedWalkExpectedKm, setEditedWalkExpectedKm] = useState<number | undefined>(undefined)
  const [isSavingEdit, setIsSavingEdit] = useState(false)
  const [newWalkWaypoints, setNewWalkWaypoints] = useState<Waypoint[]>([])
  const [editedWalkWaypoints, setEditedWalkWaypoints] = useState<Waypoint[]>([])
  const [showWaypointManager, setShowWaypointManager] = useState(false)
  const [showEditWaypointManager, setShowEditWaypointManager] = useState(false)

  // Load walks on component mount
  useEffect(() => {
    loadWalks()
  }, [])

  // Clear cached activities when prop changes
  useEffect(() => {
    if (clearActivitiesCache) {
      setWalkActivities({})
      setExpandedWalkId(null)
    }
  }, [clearActivitiesCache])

  const loadWalks = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const loadedWalks = await walkService.getAllWalks()
      setWalks(loadedWalks)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load walks')
      console.error('Failed to load walks:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateWalk = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newWalkName.trim()) return

    setIsCreating(true)
    try {
      const newWalkData = {
        name: newWalkName.trim(),
        description: '',
        published: false,
        total_days: 0,
        total_distance_km: 0,
        total_elevation_gain_m: 0,
        start_date: '',
        end_date: '',
        bounds: {
          north: 0,
          south: 0,
          east: 0,
          west: 0
        },
        waypoints: newWalkWaypoints,
        expected_km: newWalkExpectedKm
      }

      const createdWalk = await walkService.createWalk(newWalkData)
      setWalks([createdWalk, ...walks])
      setNewWalkName('')
      setNewWalkExpectedKm(undefined)
      setNewWalkWaypoints([])
      setShowWaypointManager(false)
      setShowCreateForm(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create walk')
      console.error('Failed to create walk:', err)
    } finally {
      setIsCreating(false)
    }
  }

  const handleTogglePublished = async (walkId: string) => {
    const walk = walks.find(w => w.id === walkId)
    if (!walk) return

    try {
      const updatedWalk = await walkService.updateWalk(walkId, { 
        published: !walk.published 
      })
      setWalks(walks.map(w => w.id === walkId ? updatedWalk : w))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update walk')
      console.error('Failed to update walk:', err)
    }
  }

  const handleDeleteWalk = async (walkId: string) => {
    if (!window.confirm('Are you sure you want to delete this walk? This action cannot be undone.')) {
      return
    }

    try {
      await walkService.deleteWalk(walkId)
      setWalks(walks.filter(walk => walk.id !== walkId))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete walk')
      console.error('Failed to delete walk:', err)
    }
  }

  const handleToggleExpand = async (walkId: string) => {
    if (expandedWalkId === walkId) {
      setExpandedWalkId(null)
      return
    }

    setExpandedWalkId(walkId)

    if (!walkActivities[walkId]) {
      setLoadingActivities(prev => ({ ...prev, [walkId]: true }))
      try {
        const result = await activityService.getWalkActivities(walkId)
        setWalkActivities(prev => ({ ...prev, [walkId]: result.activities }))
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load activities')
        console.error('Failed to load activities:', err)
      } finally {
        setLoadingActivities(prev => ({ ...prev, [walkId]: false }))
      }
    }
  }

  const handleStartEdit = (walk: Walk) => {
    setEditingWalkId(walk.id)
    setEditedWalkName(walk.name)
    setEditedWalkExpectedKm(walk.expected_km)
    setEditedWalkWaypoints(walk.waypoints || [])
    setShowEditWaypointManager(true) // Show waypoints by default when editing
  }

  const handleCancelEdit = () => {
    setEditingWalkId(null)
    setEditedWalkName('')
    setEditedWalkExpectedKm(undefined)
    setEditedWalkWaypoints([])
    setShowEditWaypointManager(false)
  }

  const handleSaveEdit = async (walkId: string) => {
    if (!editedWalkName.trim()) return

    setIsSavingEdit(true)
    try {
      const updatedWalk = await walkService.updateWalk(walkId, { 
        name: editedWalkName.trim(),
        expected_km: editedWalkExpectedKm,
        waypoints: editedWalkWaypoints
      })
      setWalks(walks.map(w => w.id === walkId ? updatedWalk : w))
      setEditingWalkId(null)
      setEditedWalkName('')
      setEditedWalkExpectedKm(undefined)
      setEditedWalkWaypoints([])
      setShowEditWaypointManager(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update walk')
      console.error('Failed to update walk:', err)
    } finally {
      setIsSavingEdit(false)
    }
  }

  const handleDeleteActivity = async (activity: WalkActivity, walkId: string) => {
    if (!window.confirm('Are you sure you want to delete this activity? This action cannot be undone.')) {
      return
    }

    try {
      // Use the activity's database ID for deletion
      if (activity.id && typeof activity.id === 'number') {
        await activityService.deleteActivity(activity.id)
      } else if (activity.strava_activity_id) {
        // Fallback to strava_activity_id if database ID is not available
        await activityService.deleteActivityByStravaId(activity.strava_activity_id)
      } else {
        throw new Error('Unable to identify activity to delete')
      }

      // Remove the activity from the local state
      setWalkActivities(prev => ({
        ...prev,
        [walkId]: prev[walkId]?.filter(act => act.id !== activity.id) || []
      }))
      
      // Reload walks to update counts
      await loadWalks()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete activity')
      console.error('Failed to delete activity:', err)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Walk Management</h3>
            <p className="text-slate-600">Loading walks...</p>
          </div>
        </div>
        <div className="bg-white/40 backdrop-blur-lg rounded-2xl border border-white/20 shadow-lg p-8 text-center">
          <svg className="animate-spin w-8 h-8 text-slate-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-slate-500">Loading your walks...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {error && (
        <div className="bg-red-50/80 border border-red-200/60 rounded-xl p-4">
          <div className="flex items-center space-x-3">
            <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="text-xs text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Header with Create Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-slate-900">Walk Management</h3>
          <p className="text-slate-600">Create and manage your multi-day hiking trips</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 text-white font-medium rounded-xl transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Walk
        </button>
      </div>

      {/* Create Walk Form */}
      {showCreateForm && (
        <div className="bg-white/60 backdrop-blur-lg rounded-2xl border border-white/20 shadow-lg p-6">
          <h4 className="text-lg font-bold text-slate-900 mb-4">Create New Walk</h4>
          <form onSubmit={handleCreateWalk} className="space-y-4">
            <div>
              <label htmlFor="walkName" className="block text-sm font-medium text-slate-700 mb-2">
                Walk Name
              </label>
              <input
                id="walkName"
                type="text"
                value={newWalkName}
                onChange={(e) => setNewWalkName(e.target.value)}
                placeholder="e.g., Bibbulmun Track - Section 1"
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                required
              />
            </div>
            
            <div>
              <label htmlFor="expectedKm" className="block text-sm font-medium text-slate-700 mb-2">
                Expected Distance (km) - Optional
              </label>
              <input
                id="expectedKm"
                type="number"
                value={newWalkExpectedKm || ''}
                onChange={(e) => setNewWalkExpectedKm(e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder="e.g., 1000"
                step="0.1"
                min="0"
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
              />
              <p className="text-xs text-slate-500 mt-1">Set the expected total distance to show progress percentage</p>
            </div>
            
            {/* Add Waypoints Section */}
            <div>
              <button
                type="button"
                onClick={() => setShowWaypointManager(!showWaypointManager)}
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{showWaypointManager ? 'Hide' : 'Add'} Waypoints (Optional)</span>
              </button>
              
              {showWaypointManager && (
                <div className="mt-4">
                  <WaypointManager
                    initialWaypoints={newWalkWaypoints}
                    onWaypointsChange={setNewWalkWaypoints}
                  />
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                type="submit"
                disabled={isCreating || !newWalkName.trim()}
                className="inline-flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isCreating ? 'Creating...' : 'Create Walk'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false)
                  setNewWalkName('')
                }}
                className="inline-flex items-center px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Walks List - Table Layout */}
      <div className="bg-white/60 backdrop-blur-lg rounded-2xl border border-white/20 shadow-lg overflow-hidden">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h4 className="text-lg font-bold text-slate-900">Your Walks</h4>
              <p className="text-slate-600">Manage your multi-day hiking adventures</p>
            </div>
            <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
              {walks.length} {walks.length === 1 ? 'walk' : 'walks'}
            </span>
          </div>
          
          {walks.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-12 h-12 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              <h4 className="text-lg font-medium text-slate-600 mb-2">No walks created yet</h4>
              <p className="text-slate-500 text-sm">Create your first multi-day hiking trip to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {walks.map((walk, index) => (
                <div key={walk.id} className={`rounded-xl hover:bg-white/40 transition-all duration-200 ${index !== walks.length - 1 ? 'border-b border-slate-200/30' : ''}`}>
                  <div className="flex items-center justify-between py-4 px-6">
                    <div className="flex items-center space-x-4 flex-1">
                      <button
                        onClick={() => handleToggleExpand(walk.id)}
                        className="text-slate-500 hover:text-slate-700 transition-colors duration-200"
                      >
                        <svg 
                          className={`w-5 h-5 transition-transform duration-200 ${
                            expandedWalkId === walk.id ? 'rotate-90' : ''
                          }`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-1">
                          {editingWalkId === walk.id ? (
                            <div className="flex-1 space-y-2">
                              <input
                                type="text"
                                value={editedWalkName}
                                onChange={(e) => setEditedWalkName(e.target.value)}
                                className="w-full text-lg font-bold text-slate-900 bg-white border border-slate-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                placeholder="Walk name"
                                autoFocus
                              />
                              <input
                                type="number"
                                value={editedWalkExpectedKm || ''}
                                onChange={(e) => setEditedWalkExpectedKm(e.target.value ? parseFloat(e.target.value) : undefined)}
                                className="w-full text-sm text-slate-700 bg-white border border-slate-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                placeholder="Expected distance (km)"
                                step="0.1"
                                min="0"
                              />
                            </div>
                          ) : (
                            <h4 className="text-lg font-bold text-slate-900">{walk.name}</h4>
                          )}
                          {walk.waypoints && walk.waypoints.length > 0 && (
                            <span className="inline-flex items-center px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              {walk.waypoints.length} {walk.waypoints.length === 1 ? 'Waypoint' : 'Waypoints'}
                            </span>
                          )}
                          {walk.published ? (
                            <span className="inline-flex items-center px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                              <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full mr-1"></div>
                              Published
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                              <div className="w-1.5 h-1.5 bg-slate-400 rounded-full mr-1"></div>
                              Draft
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-6 text-sm text-slate-500">
                          <span className="flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {walk.total_days} {walk.total_days === 1 ? 'day' : 'days'}
                          </span>
                          <span className="flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                            {walk.total_distance_km} km
                            {walk.expected_km && (
                              <span className="ml-1 text-emerald-600 font-medium">
                                ({Math.round((walk.total_distance_km / walk.expected_km) * 100)}%)
                              </span>
                            )}
                          </span>
                          <span className="flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                            </svg>
                            {walk.total_elevation_gain_m}m elevation
                          </span>
                          <span className="flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
                            </svg>
                            {walk.total_activities || 0} {walk.total_activities === 1 ? 'activity' : 'activities'}
                          </span>
                          {walk.waypoints && walk.waypoints.length > 0 && walk.waypoints[walk.waypoints.length - 1].name && (
                            <span className="flex items-center text-amber-600">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              → {walk.waypoints[walk.waypoints.length - 1].name!.split(',')[0]}
                            </span>
                          )}
                          <span className="text-xs text-slate-400">
                            Created {new Date(walk.created_date).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {editingWalkId === walk.id ? (
                        <>
                          <button
                            onClick={() => setShowEditWaypointManager(!showEditWaypointManager)}
                            className="inline-flex items-center space-x-2 px-3 py-2 bg-amber-100 hover:bg-amber-200 text-amber-700 text-sm font-medium rounded-lg transition-colors duration-200"
                            title="Manage Waypoints"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>{showEditWaypointManager ? 'Hide' : 'Show'} Waypoints ({editedWalkWaypoints.length})</span>
                          </button>
                          <button
                            onClick={() => handleSaveEdit(walk.id)}
                            disabled={isSavingEdit || !editedWalkName.trim()}
                            className="inline-flex items-center px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isSavingEdit ? (
                              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="inline-flex items-center px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-colors duration-200"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleTogglePublished(walk.id)}
                            className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                              walk.published
                                ? 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                                : 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700'
                            }`}
                          >
                            {walk.published ? 'Unpublish' : 'Publish'}
                          </button>
                          <button
                            onClick={() => handleStartEdit(walk)}
                            className="inline-flex items-center px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 text-sm font-medium rounded-lg transition-colors duration-200"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteWalk(walk.id)}
                            className="inline-flex items-center px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 text-sm font-medium rounded-lg transition-colors duration-200"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Activities Section */}
                  {/* Edit Endpoint Section */}
                  {editingWalkId === walk.id && showEditWaypointManager && (
                    <div className="px-6 pb-4">
                      <div className="ml-9">
                        <WaypointManager
                          initialWaypoints={editedWalkWaypoints}
                          onWaypointsChange={setEditedWalkWaypoints}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Expanded Activities Section */}
                  {expandedWalkId === walk.id && (
                    <div className="px-6 pb-4">
                      <div className="ml-9 bg-slate-50/50 rounded-lg p-4">
                        <h5 className="text-sm font-semibold text-slate-700 mb-3 flex items-center">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
                          </svg>
                          Activities in this walk
                        </h5>
                        
                        {loadingActivities[walk.id] ? (
                          <div className="flex items-center justify-center py-4">
                            <svg className="animate-spin w-5 h-5 text-slate-400 mr-2" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-sm text-slate-500">Loading activities...</span>
                          </div>
                        ) : walkActivities[walk.id]?.length === 0 ? (
                          <div className="text-center py-6">
                            <svg className="w-8 h-8 text-slate-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
                            </svg>
                            <p className="text-sm text-slate-500">No activities in this walk yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {walkActivities[walk.id]?.sort((a, b) => new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime()).map((activity, activityIndex) => (
                              <div key={activity.id || activityIndex} className="flex items-center justify-between bg-white/60 rounded-lg p-3">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3 mb-1">
                                    <span className="text-xs font-medium text-slate-500 bg-slate-200 rounded-full px-2 py-1">
                                      Activity {activityIndex + 1}
                                    </span>
                                    <span className="text-sm font-medium text-slate-700">
                                      {activity.name || `Activity on ${new Date(activity.date).toLocaleDateString()}`}
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-4 text-xs text-slate-500">
                                    <span className="flex items-center">
                                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                      </svg>
                                      {activity.distance_km.toFixed(1)} km
                                    </span>
                                    <span className="flex items-center">
                                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                                      </svg>
                                      {activity.elevation_gain_m}m
                                    </span>
                                    <span className="flex items-center">
                                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      {activity.duration_hours.toFixed(1)}h
                                    </span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleDeleteActivity(activity, walk.id)}
                                  className="flex items-center px-2 py-1 bg-red-100 hover:bg-red-200 text-red-600 text-xs font-medium rounded transition-colors duration-200"
                                >
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Delete
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default WalkManagement